import * as React from 'react'

import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import {
  Radio,
  RadioGroup,
} from '~/client/src/shared/components/shadcn-ui/radio-group'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { cn } from '~/client/src/shared/utils/cn'

import DayWindowField from './DayWindowField'

/** One weekday's row: whether it is worked, and the window it works. */
export interface IWeekdayWindow {
  /** 1 = Sunday .. 7 = Saturday, matching the server's `dayOfWeek`. */
  dayOfWeek: number
  isWorking: boolean
  startMinutes: number
  endMinutes: number
}

export enum WeekdayScheduleMode {
  Everyday = 'everyday',
  Custom = 'custom',
}

interface IProps {
  days: IWeekdayWindow[]
  mode: WeekdayScheduleMode
  everydayStartMinutes: number
  everydayEndMinutes: number
  projectDateStore: ProjectDateStore
  onModeChange: (mode: WeekdayScheduleMode) => void
  onToggleDay: (dayOfWeek: number) => void
  onDayWindowChange: (
    dayOfWeek: number,
    startMinutes: number,
    endMinutes: number,
  ) => void
  onEverydayWindowChange: (startMinutes: number, endMinutes: number) => void
  portalContainer?: React.ComponentProps<
    typeof DayWindowField
  >['portalContainer']
}

const TOGGLE_ROW_CLASS = 'tw:flex tw:cursor-pointer tw:items-center tw:gap-2'

/**
 * Wide enough for a formatted 12-hour range plus its chevron, and fixed so every
 * row's window starts at the same x whatever its label is — the indented weekday
 * rows would otherwise sit a checkbox's width off from Everyday.
 */
const WINDOW_COLUMN_CLASS = 'tw:w-[248px] tw:shrink-0'

/**
 * The Daily tab's Everyday/Custom choice and, under Custom, one row per weekday.
 *
 * Mirrors how a project's own working schedule is authored, because a resource
 * inherits that schedule when it states nothing of its own — the two should not
 * read as different kinds of thing.
 */
function WeekdayScheduleFields({
  days,
  mode,
  everydayStartMinutes,
  everydayEndMinutes,
  projectDateStore,
  onModeChange,
  onToggleDay,
  onDayWindowChange,
  onEverydayWindowChange,
  portalContainer,
}: IProps) {
  const { weekdayNamesList } = projectDateStore
  const isEveryday = mode === WeekdayScheduleMode.Everyday

  const getDayName = (dayOfWeek: number) => weekdayNamesList[dayOfWeek - 1]

  return (
    <RadioGroup
      className="tw:w-full tw:gap-4"
      value={mode}
      onValueChange={value => onModeChange(value as WeekdayScheduleMode)}
    >
      <div className="tw:flex tw:items-center tw:gap-2">
        <div className="tw:flex tw:min-w-0 tw:flex-1">
          <label
            className={cn(
              TOGGLE_ROW_CLASS,
              'tw:min-w-0 tw:max-w-full tw:truncate tw:text-app-dense',
              isEveryday ? 'tw:text-foreground' : 'tw:text-muted-foreground',
            )}
          >
            <Radio value={WeekdayScheduleMode.Everyday} />
            {Localization.translator.everyday}
          </label>
        </div>
        {isEveryday && (
          <div className={WINDOW_COLUMN_CLASS}>
            <DayWindowField
              startMinutes={everydayStartMinutes}
              endMinutes={everydayEndMinutes}
              projectDateStore={projectDateStore}
              onChange={onEverydayWindowChange}
              ariaLabel={Localization.translator.everyday}
              portalContainer={portalContainer}
            />
          </div>
        )}
      </div>

      <label
        className={cn(
          TOGGLE_ROW_CLASS,
          'tw:w-fit tw:text-app-dense',
          isEveryday ? 'tw:text-muted-foreground' : 'tw:text-foreground',
        )}
      >
        <Radio value={WeekdayScheduleMode.Custom} />
        {Localization.translator.custom}
      </label>

      {!isEveryday && (
        <div className="tw:flex tw:w-full tw:flex-col tw:gap-3 tw:pl-2">
          {days.map(day => (
            <div
              key={day.dayOfWeek}
              className="tw:flex tw:items-center tw:gap-2"
            >
              {/* The label hugs its checkbox and name: stretched across the row
                  it would turn the gap before the window into a toggle, and a
                  stray click there would close the day rather than open its
                  times. */}
              <div className="tw:flex tw:min-w-0 tw:flex-1">
                <label
                  className={cn(
                    TOGGLE_ROW_CLASS,
                    'tw:min-w-0 tw:max-w-full tw:truncate tw:text-app-dense',
                    day.isWorking
                      ? 'tw:text-foreground'
                      : 'tw:text-muted-foreground',
                  )}
                >
                  <Checkbox
                    checked={day.isWorking}
                    onCheckedChange={() => onToggleDay(day.dayOfWeek)}
                  />
                  {getDayName(day.dayOfWeek)}
                </label>
              </div>
              {/* A weekday nobody works has no window to show: the server reads a
                  missing weekday as closed, not as unrestricted. */}
              {day.isWorking && (
                <div className={WINDOW_COLUMN_CLASS}>
                  <DayWindowField
                    startMinutes={day.startMinutes}
                    endMinutes={day.endMinutes}
                    projectDateStore={projectDateStore}
                    onChange={(startMinutes, endMinutes) =>
                      onDayWindowChange(day.dayOfWeek, startMinutes, endMinutes)
                    }
                    ariaLabel={getDayName(day.dayOfWeek)}
                    portalContainer={portalContainer}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </RadioGroup>
  )
}

export default WeekdayScheduleFields
